import type { AiSystemEditDraft } from "../../../functions/_ai/system-edit-schema";
import type { SystemDigest } from "../ai/system-digest";
import type { Issue, System } from "../types";

export const ASSISTANT_MAX_PROMPT_CHARS = 2000;
export const ASSISTANT_MAX_HISTORY_TURNS = 16;
export const ASSISTANT_MAX_NODES = 150;
export const ASSISTANT_MAX_CABLES = 400;

export type AssistantIssueReference = { code: string; nodeIds: string[]; cableIds: string[] };
export type AssistantSimulation = {
  componentStates: Record<string, string>;
  batterySoc: Record<string, number>;
  loadStates: Record<string, { active: boolean }>;
};
export type AssistantPayload = {
  requestId: string;
  conversationId: string;
  prompt: string;
  /** Drawing capability: ask is read-only; edit permits proposals when requested.
   * The assistant infers the user's intent from the conversation, not this field. */
  mode: "ask" | "edit";
  history: Array<{ role: "user" | "assistant"; content: string }>;
  system: System;
  issue?: AssistantIssueReference;
  projectId?: string | null;
  locale?: string;
  autoSizeTargetPct?: number;
  /** User-selected operating scenario, never live sensor measurements. */
  simulation?: AssistantSimulation;
};

export type AssistantEvidence = {
  id: string;
  title: string;
  nodeIds: string[];
  cableIds: string[];
  /** Server-rendered source text, never supplied by the model. */
  detail: string;
  source: "diagnostic" | "calculation" | "catalog" | "datasheet";
  url?: string;
  /** Retrieved source for one exact component field. */
  verifiedSpec?: { field: string; value: number | string; quote: string };
  /** Numbers must be cited through {{evidence-id.fact-name}} tokens. */
  facts?: Record<string, string | number>;
  factUnits?: Record<string, "A" | "mA" | "Ah" | "V" | "W" | "Wh" | "h" | "m" | "mm²" | "%">;
};
export type AssistantProposal = {
  baseFingerprint: string;
  specUpdates?: Array<{ nodeId: string; field: string; value: number | string; sourceUrl: string; sourceTitle: string; quote: string }>;
  autoSizeTargetPct?: number;
  topology?: AiSystemEditDraft;
  replacements?: Array<{ ref: string; catalogId: string }>;
  /** Selects an engine-authored fix, never an arbitrary wire/fuse value. */
  repairIssueIds: string[];
};
export type AssistantReply = {
  kind: "answer" | "clarification" | "proposal" | "unsupported";
  message: string;
  evidence: AssistantEvidence[];
  proposal?: AssistantProposal;
  capability: string;
  limitation?: string;
  /** A clarification may offer delegation of its pending catalog/design choice.
   * Absent or false never offers delegation of missing physical facts. */
  canChooseForMe?: boolean;
  /** Visible quick replies; clicking sends exactly this text as the next turn. */
  choices?: string[];
};
export type AssistantContext = {
  payload: AssistantPayload;
  system: System;
  fingerprint: string;
  issues: Issue[];
  evidence: AssistantEvidence[];
  digest: SystemDigest;
  refToNodeId: Map<string, string>;
  catalogIds: Set<string>;
  modelContext: string;
};
export type AssistantPreparedProposal = {
  ok: true;
  system: System;
  addedNodeIds: string[];
  removedNodeIds: string[];
  changedNodeIds: string[];
  addedCableIds: string[];
  removedCableIds: string[];
  changedCableIds: string[];
  issuesBefore: Issue[];
  issuesAfter: Issue[];
};
export type AssistantProposalResult = AssistantPreparedProposal | { ok: false; error: string };
